import { useParams, useSearchParams } from 'react-router-dom'

import { TaskBoard, TaskList } from '@/features/tasks/components'
import { SaveIndicator } from '@/shared/components/common'
import { ErrorState, SkeletonRows } from '@/shared/components/data'
import { Segmented } from '@/shared/components/ui'
import { usePersistedFlag } from '@/shared/hooks/use-persisted-flag'
import { useT } from '@/shared/i18n'

import { useProject, useTaskStages } from '../api'

type TasksView = 'lista' | 'tablero'

/** Se recuerda la vista: quien trabaja en tablero no quiere elegirlo cada día. */
const VIEW_STORAGE_KEY = 'pm-project-tasks-board'

export function ProjectTasksTab() {
  const { projectId = '' } = useParams()
  // `?nueva=1` lo pone el conmutador `⌘K`: quien eligió "Nueva tarea en X"
  // ya decidió qué va a escribir, así que llega con el cursor puesto.
  const [searchParams] = useSearchParams()
  const focusQuickAdd = searchParams.get('nueva') === '1'
  const t = useT()
  const project = useProject(projectId)
  const stages = useTaskStages()
  const [board, setBoard] = usePersistedFlag(VIEW_STORAGE_KEY, false)

  if (project.isPending || stages.isPending) return <SkeletonRows rows={6} />
  if (project.isError) {
    return <ErrorState error={project.error} onRetry={() => void project.refetch()} />
  }

  const view: TasksView = board ? 'tablero' : 'lista'
  const views: readonly { value: TasksView; label: string }[] = [
    { value: 'lista', label: t('projects.tasks.viewList') },
    { value: 'tablero', label: t('projects.tasks.viewBoard') },
  ]

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Segmented
          label={t('projects.tasks.viewLabel')}
          value={view}
          options={views}
          onChange={(next) => setBoard(next === 'tablero')}
        />
        <SaveIndicator />
      </div>

      {/*
        Las dos vistas leen la MISMA query y el MISMO rank: mover una tarjeta
        arriba en el tablero la sube también en la lista (plan 2.3).
      */}
      {view === 'tablero' ? (
        <TaskBoard projectId={projectId} stages={stages.data ?? []} />
      ) : (
        <TaskList projectId={projectId} stages={stages.data ?? []} focusQuickAdd={focusQuickAdd} />
      )}
    </div>
  )
}
